import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database, Json } from '@/lib/supabase/types';
import { readChannel, readMessage, parseMessage, type MessageChannel, type MessageError, type RegistrationMessage } from './message';

/**
 * The builder's reads and writes of messages after registration, with the
 * organizer's own browser client under RLS (organizers only). Participants
 * never read registration_messages; they call get_my_registration_message.
 */
type Client = SupabaseClient<Database>;

export interface StoredMessage {
  message: RegistrationMessage;
  channels: MessageChannel;
}

export interface RegistrationMessages {
  event: StoredMessage | null;
  /** ticket id → that ticket's own message; a ticket without one uses the event's. */
  tickets: Record<string, StoredMessage>;
  /** For the preview's "Message from the organizer – <name>". */
  organizerName: string;
}

export async function loadRegistrationMessages(supabase: Client, eventId: string): Promise<RegistrationMessages> {
  const [{ data, error }, { data: content }] = await Promise.all([
    supabase.from('registration_messages').select('ticket_id, body, channels').eq('content_id', eventId),
    supabase.from('content').select('organizer_profiles(name)').eq('id', eventId).maybeSingle(),
  ]);
  if (error) throw new Error(`loadRegistrationMessages: read failed: ${error.message}`);
  const profile = (content as { organizer_profiles?: { name?: string | null } | null } | null)?.organizer_profiles;
  const result: RegistrationMessages = { event: null, tickets: {}, organizerName: profile?.name?.trim() || '' };
  for (const row of data ?? []) {
    const message = readMessage(row.body);
    if (!message) continue;
    const stored = { message, channels: readChannel(row.channels) };
    if (row.ticket_id) result.tickets[row.ticket_id] = stored;
    else result.event = stored;
  }
  return result;
}

/** Saves the event message (ticketId null) or a ticket's own; replaces what was there. */
export async function saveRegistrationMessage(
  supabase: Client,
  input: { eventId: string; ticketId: string | null; message: RegistrationMessage; channels: MessageChannel },
): Promise<'saved' | { invalid: MessageError }> {
  const checked = parseMessage(input.message);
  if (!checked.ok) return { invalid: checked.error };
  const { data, error } = await supabase
    .from('registration_messages')
    .upsert(
      { content_id: input.eventId, ticket_id: input.ticketId, body: checked.message as unknown as Json, channels: readChannel(input.channels) },
      { onConflict: 'content_id,ticket_id' },
    )
    .select('id');
  if (error) throw new Error(`saveRegistrationMessage: write failed: ${error.message}`);
  // RLS refusals write nothing and say nothing.
  if (!data || data.length === 0) throw new Error('saveRegistrationMessage: nothing saved');
  return 'saved';
}

/** Deletes the event message, or a ticket's own (the ticket then uses the event's again). */
export async function deleteRegistrationMessage(
  supabase: Client,
  input: { eventId: string; ticketId: string | null },
): Promise<void> {
  let query = supabase.from('registration_messages').delete().eq('content_id', input.eventId);
  query = input.ticketId ? query.eq('ticket_id', input.ticketId) : query.is('ticket_id', null);
  const { error } = await query;
  if (error) throw new Error(`deleteRegistrationMessage: delete failed: ${error.message}`);
}

/** The caller's pop-up message (platform channel), or null. */
export async function fetchMyRegistrationMessage(supabase: SupabaseClient, contentId: string): Promise<RegistrationMessage | null> {
  const { data, error } = await supabase.rpc('get_my_registration_message' as any, { p_content_id: contentId });
  if (error) throw error;
  return readMessage(data);
}
